import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import procurementApi from "../services/procurementApi";
import AddVendorPOModal from "../components/AddVendorPOModal";

const sanitizeReturnTo = (returnTo, fallbackPath) => {
  if (!returnTo || typeof returnTo !== "object") {
    return { pathname: fallbackPath, search: "", hash: "" };
  }

  const nextSearchParams = new URLSearchParams(returnTo.search || "");
  nextSearchParams.delete("openVendorQuotation");
  const search = nextSearchParams.toString();

  return {
    pathname: returnTo.pathname || fallbackPath,
    search: search ? `?${search}` : "",
    hash: returnTo.hash || "",
  };
};

const VendorPOCreatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const fallbackReturnTo = useMemo(
    () => ({
      pathname: id ? `/procurement/requests/${id}` : "/procurement/requests",
      search: "",
      hash: "",
    }),
    [id],
  );

  const returnTo = useMemo(
    () => sanitizeReturnTo(location.state?.returnTo, fallbackReturnTo.pathname),
    [fallbackReturnTo.pathname, location.state?.returnTo],
  );

  const navigateBack = useCallback(() => {
    navigate(`${returnTo.pathname}${returnTo.search}${returnTo.hash}`, {
      replace: true,
      state: undefined,
    });
  }, [navigate, returnTo]);

  useEffect(() => {
    let isMounted = true;

    const fetchRequest = async () => {
      try {
        setLoading(true);
        setLoadError("");
        const response = await procurementApi.getRequestById(id);
        const requestData = response?.data || response;

        if (!requestData) {
          throw new Error("Request not found");
        }

        if (isMounted) {
          setRequest(requestData);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.response?.data?.message || error.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRequest();

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        px={{ base: 4, md: 6, xl: 8 }}
        py={{ base: 4, md: 6 }}
      >
        <Stack spacing={4} maxW="7xl" mx="auto">
          <Skeleton height="48px" borderRadius="xl" />
          <Skeleton height="120px" borderRadius="xl" />
          <Skeleton height="420px" borderRadius="xl" />
        </Stack>
      </Box>
    );
  }

  if (loadError || !request) {
    return (
      <Box
        minH="100vh"
        bg="gray.50"
        px={{ base: 4, md: 6, xl: 8 }}
        py={{ base: 6, md: 10 }}
      >
        <Box
          maxW="4xl"
          mx="auto"
          bg="white"
          borderRadius="2xl"
          boxShadow="xl"
          p={{ base: 5, md: 8 }}
        >
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">Unable to open vendor quotation</Text>
              <Text fontSize="sm">
                {loadError || "The procurement request could not be loaded."}
              </Text>
            </Box>
          </Alert>
          <HStack mt={5} justify="flex-end">
            <Button onClick={navigateBack}>Back to request</Button>
          </HStack>
        </Box>
      </Box>
    );
  }

  return (
    <AddVendorPOModal
      renderMode="page"
      requestId={id}
      requestStatus={request.status}
      proposalStatus={request.status}
      requestLineItems={request.lineItems || []}
      onClose={navigateBack}
      onSuccess={navigateBack}
    />
  );
};

export default VendorPOCreatePage;
